import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, CalendarDays, User } from 'lucide-react';
import API from '../../api/axiosInstance';
import { addDaysToLADateKey, formatLADateRangeArrow } from '../../utils/laDateUtils';
import ParticipantBookingPanel from './ParticipantBookingPanel';

/**
 * One booked room within a stay period. Owns its own panel/cancel state so
 * editing or cancelling one segment never affects its siblings in the same period.
 */
const RoomSegmentCard = ({ group, stayInKey, stayOutKey, competitionId, participantId, guests = [], onChanged }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const guestNames = (group.selected_guest_ids || [])
    .map(String)
    .map((id) => guests.find((g) => g._id === id))
    .filter(Boolean)
    .map((g) => `${g.firstName || ''} ${g.lastName || ''}`.trim());

  const handleDone = () => {
    setPanelOpen(false);
    onChanged();
  };

  const handleCancel = async () => {
    setConfirmCancelOpen(false);
    if (!group?.booking_ids?.length) return;
    setCancelling(true);
    setCancelError('');
    const failures = [];
    for (const id of group.booking_ids) {
      try {
        await API.delete(`/ems/room-bookings/${id}`);
      } catch (err) {
        failures.push(err.response?.data?.message || 'Failed');
      }
    }
    await onChanged();
    setCancelling(false);
    if (failures.length) {
      setCancelError(`Some nights failed to cancel: ${failures.join('; ')}`);
    }
  };

  return (
    <div className="pbt-segment">
      <div className="pbt-segment__row">
        <div className="pbt-segment__content">
          <div className="pbt-segment__where">
            <Check size={14} strokeWidth={2.5} className="pbt-confirmed-icon" />
            <strong>{group.hotel_name}</strong>
            <span className="pbt-confirmed-detail__sep">·</span>
            {group.room_type} {group.room_label}
            {group.config_name && (
              <span className="pbt-segment__config-name">{group.config_name}</span>
            )}
          </div>

          <div className="pbt-confirmed-detail__meta">
            <span className={`pbt-status-chip ${group.allocation_type === 'private' ? 'pbt-status-chip--private' : 'pbt-status-chip--required'}`}>
              <span className="pbt-status-chip__dot" />
              {group.allocation_type === 'private' ? 'Private' : 'Required'}
            </span>
            <span>${group.price}/night</span>
            <span>{formatLADateRangeArrow(group.from_date, addDaysToLADateKey(group.to_date, 1))}</span>
            <span>{group.nights} night{group.nights === 1 ? '' : 's'}</span>
            <span className="pbt-confirmed-detail__total">
              Total ${(Number(group.price || 0) * (group.nights || 0)).toLocaleString()}
            </span>
          </div>

          {(group.is_guest_only || guestNames.length > 0) && (
            <div className="pbt-confirmed-detail__guests">
              {group.is_guest_only ? (
                <>
                  <span className="pbt-guest-badge">GUEST ONLY</span>
                  {guestNames.length > 0 && (
                    <span className="pbt-guest-pill">
                      <User size={9} strokeWidth={2.5} />
                      {guestNames.length} guest{guestNames.length !== 1 ? 's' : ''}: {guestNames.join(', ')}
                    </span>
                  )}
                </>
              ) : (
                <span className="pbt-guest-pill">
                  <User size={9} strokeWidth={2.5} />
                  1 participant + {guestNames.length} guest{guestNames.length !== 1 ? 's' : ''}: {guestNames.join(', ')}
                </span>
              )}
            </div>
          )}
        </div>

        {!panelOpen && (
          confirmCancelOpen ? (
            <div className="pbt-inline-cancel">
              <span>Cancel this booking? This cannot be undone.</span>
              <div className="pbt-inline-cancel__actions">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmCancelOpen(false)}>
                  Keep
                </button>
                <button type="button" className="btn btn-sm btn-danger" onClick={handleCancel} disabled={cancelling}>
                  {cancelling ? 'Cancelling…' : 'Cancel Booking'}
                </button>
              </div>
            </div>
          ) : (
            <div className="pbt-stay-card__actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  navigate(`/ems/bookings?hotel_id=${group.hotel_id}&room_id=${group.room_id}&date=${group.from_date}`)
                }
              >
                <CalendarDays size={14} /> View in Calendar
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPanelOpen(true)}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => setConfirmCancelOpen(true)}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Cancel'}
              </button>
            </div>
          )
        )}
      </div>

      {cancelError && <div className="pbt-panel__error">{cancelError}</div>}

      {panelOpen && (
        <ParticipantBookingPanel
          mode="edit"
          stayInKey={stayInKey}
          stayOutKey={stayOutKey}
          competitionId={competitionId}
          participantId={participantId}
          group={group}
          guests={guests}
          onDone={handleDone}
        />
      )}
    </div>
  );
};

export default RoomSegmentCard;
